import { pathOr } from 'ramda';
import { delay, eventChannel } from 'redux-saga';
import { call, put, select, spawn, take, takeLatest } from 'redux-saga/effects';

import { queryClient } from '@frontend/api';

import notificationService from 'services/notificationService';
import socketSingleton from 'services/webSocketService';

import { getIsLoggedIn } from 'store/session/selectors';

import notificationFlash from '../notificationFlash';
import {
  acknowledgeNotification,
  entities,
  initializeNotifications,
  markAllNotificationsRead,
  notificationsConnection,
  removeNotificationsFromPageNotificationIds,
  types,
} from './actions';
import { getNotificationById, isShowingOnlyUnread } from './reducer';

export default function* root() {
  yield spawn(initNotifications);
  yield takeLatest('auth/login/LOGIN_SUCCESS', initNotifications);
  yield takeLatest(
    types.ACKNOWLEDGE_NOTIFICATION_REQUEST,
    watchAcknowledgeNotification,
  );
  yield takeLatest(types.INIT_NOTIFICATIONS_REQUEST, watchFetchNotifications);
  yield takeLatest(
    types.MARK_ALL_NOTIFICATIONS_READ_REQUEST,
    watchMarkNotificationsRead,
  );
  yield takeLatest(
    types.NOTIFICATIONS_CONNECTION_INIT_STREAM,
    watchNotifications,
  );
}

function createSocketChannel(topic) {
  return eventChannel((emit) => {
    const subscription = socketSingleton.subscribe(topic, emit);
    return subscription.unsubscribe;
  });
}

export function* watchAcknowledgeNotification({ payload }) {
  try {
    const { notification } = payload;
    const isViewingOnlyUnread = yield select(isShowingOnlyUnread);
    const response = yield call(notificationService.patch, notification.id, {
      isAcknowledged: true,
    });
    response.page = notification.page;
    yield put(entities.receiveNotifications([response]));
    yield put(acknowledgeNotification.success());
    if (isViewingOnlyUnread)
      yield put(
        removeNotificationsFromPageNotificationIds({
          idsToRemove: [notification.id],
        }),
      );
  } catch (error) {
    console.error('Failed to acknowledge notification', error);
    yield put(acknowledgeNotification.error(error));
  }
}

export function* watchNotifications() {
  const socketChannel = yield call(createSocketChannel, 'notifications');
  try {
    while (true) {
      // An error from socketChannel will cause the saga to jump to the catch block
      const message = yield take(socketChannel);

      for (const key of Object.keys(message)) {
        // this assumes that multiple notifications can be passed down with each message.
        const id = pathOr(null, ['id'], message[key]);
        const storedNotification = yield select(getNotificationById, id);

        // place in store
        yield put(entities.receiveNotifications([message[key]]));

        // only show notification flashes if the title, or body has changed
        let showNotification = true;
        if (
          storedNotification &&
          storedNotification.title == pathOr(null, [key, 'title'], message) &&
          storedNotification.body == pathOr(null, [key, 'body'], message) &&
          storedNotification.otherOccurrencesCount ==
            pathOr(null, [key, 'otherOccurrencesCount'], message)
        ) {
          showNotification = false;
        }

        if (
          !pathOr(true, ['isAcknowledged'], message[key]) &&
          showNotification
        ) {
          // fetching the thumbnail image if part file uuid
          const partFileUuid = pathOr('', ['partFileUuid'], message[key]);
          let results;
          if (partFileUuid) {
            results = yield call(
              notificationService.getThumbnail,
              partFileUuid,
            );
          }
          // trigger the flash for unacknowledged notifications and unseen notifications
          yield call(notificationFlash, {
            notification: message[key],
            thumbnailBlob: results,
          });
        }

        /*
          This delay is a hack and should be removed.
          It's purpose is to prevent duplicate notifications from being re-rendered.
          This is necessary because we can receive multiple of the same notifications in the amount of time it takes to commit one to the store.
          */
        yield delay(250);
      }

      // Wrap the call to invalidateQueries in a function so that invalidateQueries keeps its "this" context.
      yield call(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      });
    }
  } catch (error) {
    console.error('Received a socket error, terminating the channel', error);
    // If we get an error on the socket connection, or if the server
    // disconnects, we want to close the channel and terminate this saga
    // We want to open a new connection to the socket server before closing
    // this one to avoid missing any messages
    yield put(notificationsConnection.initStream());
    // The socketChannel is still open in the catch block
    // If we want to end the socketChannel, we need to close it explicitly
    socketChannel.close();
  }
}

function* watchFetchNotifications() {
  try {
    const { results } = yield call(notificationService.list);
    yield put(entities.receiveNotifications(results));
    yield put(initializeNotifications.success());
  } catch (e) {
    console.error('Error in fetch notifications', e);
    yield put(initializeNotifications.error());
  }
}

function* watchMarkNotificationsRead(action) {
  const { payload } = action;
  const { callback } = payload;
  try {
    const isViewingOnlyUnread = yield select(isShowingOnlyUnread);
    const results = yield call(notificationService.markAllRead);
    yield put(entities.receiveNotifications(results));
    yield put(markAllNotificationsRead.success());
    if (callback) {
      yield call(callback, results);
    }

    if (isViewingOnlyUnread) {
      yield put(
        removeNotificationsFromPageNotificationIds({
          idsToRemove: results.map((n) => n.id),
        }),
      );
    }
  } catch (e) {
    yield put(markAllNotificationsRead.error());
  }
}

function* initNotifications() {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (isLoggedIn) {
    yield put(initializeNotifications.request());
    yield put(notificationsConnection.initStream());
  }
}
